/**
 * 消費税計算関連のcomposable
 */
export default function useTaxCalculation() {
  /**
   * 旧キー名から新キー名へのマッピング（後方互換性のため）
   * TODO: バックエンドリリース完了後に削除
   */
  const LEGACY_TAX_RATE_MAPPING = {
    eight_percent: 'reduced',
    ten_percent: 'standard',
    tax_exempt: 'exempt',
  }

  /**
   * 新キー名から旧キー名へのマッピング（API送信用）
   * TODO: バックエンドリリース完了後に削除
   */
  const NEW_TO_LEGACY_TAX_RATE_MAPPING = {
    reduced: 'eight_percent',
    standard: 'ten_percent',
    exempt: 'tax_exempt',
  }

  /**
   * 税率キーを正規化（旧キー名を新キー名に変換）
   * @param {string} taxRate - 税率（新旧どちらのenum値でも可）
   * @returns {string} 正規化された税率
   */
  const normalizeTaxRate = taxRate => {
    return LEGACY_TAX_RATE_MAPPING[taxRate] || taxRate
  }

  /**
   * 税率キーをAPI送信用に変換（新キー名を旧キー名に変換）
   * TODO: バックエンドリリース完了後に削除し、そのまま返すように変更
   * @param {string} taxRate - 税率（新キー名）
   * @returns {string} API送信用の税率（旧キー名）
   */
  const toLegacyTaxRate = taxRate => {
    return NEW_TO_LEGACY_TAX_RATE_MAPPING[taxRate] || taxRate
  }

  /**
   * 税率の選択肢
   */
  const taxRateOptions = [
    { text: '', value: 'blank' },
    { text: '8%', value: 'reduced' },
    { text: '10%', value: 'standard' },
    { text: '非課税', value: 'exempt' },
  ]

  /**
   * 税率から税額を計算（内税計算）
   * @param {number} amount - 金額
   * @param {string} taxRate - 税率（新旧どちらのenum値でも可）
   * @returns {number|null} 消費税額（ブランクの場合はnull）
   */
  const calculateTaxAmount = (amount, taxRate) => {
    if (!amount || !taxRate) return null

    const normalizedRate = normalizeTaxRate(taxRate)

    switch (normalizedRate) {
      case 'blank':
        return null // ブランクで表示
      case 'standard':
        // 標準税率（現在10%）: 金額 / 110 * 10
        return Math.floor((amount / 110) * 10)
      case 'reduced':
        // 軽減税率（現在8%）: 金額 / 108 * 8
        return Math.floor((amount / 108) * 8)
      case 'exempt':
        return 0 // 非課税
      default:
        return null
    }
  }

  /**
   * 税率の表示名を取得
   * @param {string} taxRate - 税率（新旧どちらのenum値でも可）
   * @returns {string} 表示名
   */
  const getTaxRateText = taxRate => {
    const normalizedRate = normalizeTaxRate(taxRate)
    const found = taxRateOptions.find(opt => opt.value === normalizedRate)

    return found ? found.text : ''
  }

  /**
   * 税額を表示用にフォーマット
   * @param {number|null} taxAmount - 消費税額
   * @returns {string} 表示用文字列
   */
  const formatTaxAmount = taxAmount => {
    if (taxAmount === null || taxAmount === undefined) return ''
    if (taxAmount === 0) return '¥0'

    return `¥${taxAmount.toLocaleString('ja-JP')}`
  }

  /**
   * 税額の値を取得（テンプレート用）
   * @param {Object} item - 経費アイテム
   * @returns {number|null} 消費税額
   */
  const getTaxAmountValue = item => {
    return calculateTaxAmount(item.amount, item.taxRate)
  }

  /**
   * 税額の表示文字列を取得（テンプレート用）
   * @param {Object} item - 経費アイテム
   * @returns {string} 表示用文字列
   */
  const getTaxAmountDisplay = item => {
    const taxAmount = calculateTaxAmount(item.amount, item.taxRate)

    return formatTaxAmount(taxAmount)
  }

  return {
    taxRateOptions,
    normalizeTaxRate,
    toLegacyTaxRate,
    calculateTaxAmount,
    getTaxRateText,
    formatTaxAmount,
    getTaxAmountValue,
    getTaxAmountDisplay,
  }
}
